import { JsonPipe } from '@angular/common';
import { Component, inject, input, output, signal } from '@angular/core';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { MarkdownLazyModule } from '@app/markdown-lazy.module';
import type {
    ChatPart,
    ChatToolPart,
    ChatToolStatus,
} from '@app/shared/components/ai-chat/chat-message.model';
import { ClipboardService } from '@app/shared/services/clipboard.service';

@Component({
    selector: 'cor-ai-chat-message-parts',
    templateUrl: './ai-chat-message-parts.component.html',
    styleUrls: ['./ai-chat-message-parts.component.scss'],
    imports: [JsonPipe, TooltipModule, MarkdownLazyModule],
})
export class AiChatMessagePartsComponent {
    readonly parts = input.required<ChatPart[]>();
    readonly rendered = output<void>();

    private readonly clipboardService = inject(ClipboardService);
    private readonly expandedParts = signal<Set<string>>(new Set());

    protected isExpanded(id: string): boolean {
        return this.expandedParts().has(id);
    }

    protected toggleExpanded(id: string): void {
        this.expandedParts.update(set => {
            const next = new Set(set);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    }

    protected copyToolOutput(part: ChatToolPart): void {
        const text = part.state.output ?? part.state.error ?? part.state.title ?? part.tool;
        this.clipboardService.copyToClipboard(text);
    }

    protected toolStatusIcon(status: ChatToolStatus): string {
        switch (status) {
            case 'completed':
                return 'pi pi-check-circle';
            case 'error':
                return 'pi pi-times-circle';
            default:
                return 'pi pi-spinner';
        }
    }

    protected isToolActive(status: ChatToolStatus): boolean {
        return status === 'pending' || status === 'running';
    }

    protected toolLabel(part: ChatToolPart): string {
        return part.state.title || part.tool;
    }

    protected hasToolDetails(part: ChatToolPart): boolean {
        const { input, output, error } = part.state;

        return Boolean(input || output || error);
    }
}
